import { useCallback } from "react";
import dayjs from "dayjs";
import api from "../../../services/api/axios";

const PROSPECTING_TAG_ALIASES = {
  "🎯 Target Industry": "Target Industry",
  "Lead – High Intent": "High Intent",
  "Lead – Low Intent": "Low Intent",
};

function normalizeProspectingTags(tags = []) {
  if (!Array.isArray(tags)) return [];

  const seen = new Set();
  const normalized = [];

  tags.forEach((value) => {
    const rawTag = String(value || "").trim();
    const tag = PROSPECTING_TAG_ALIASES[rawTag] || rawTag;
    if (!tag || seen.has(tag)) return;
    seen.add(tag);
    normalized.push(tag);
  });

  return normalized;
}

export default function useWebsiteVisitActions({
  sortedRows,
  selectedIds,
  getGroupId,
  cleanDisplay,
  getNaicsFields,
  getStateDisplay,
  deriveCountry,
  getSector,
  normalizeGuid,
  normalizeLocationForBRE,
  showSnackbar,
}) {
  const mapDynamicsContactToBREContact = useCallback((c) => {
    if (!c) return null;

    return {
      name: c.fullname || "Unknown",
      email: c.email || null,
      direct_phone_number: c.phone || null,
      cell_phone_number: null,
      job_title: c.jobtitle || null,
      linkedin: c.linkedin || null,

      city: c.city || null,
      state: c.state || null,
      country: c.country || null,
    };
  }, []);

  const getSelectedGroups = useCallback(() => {
    return sortedRows.filter((group) => {
      const id = getGroupId(group);
      return id && selectedIds.has(id);
    });
  }, [sortedRows, selectedIds, getGroupId]);

  const handleExportSelected = useCallback(async () => {
    const selectedGroups = getSelectedGroups();

    if (selectedGroups.length === 0) return;

    const rowsForExcel = selectedGroups.map((group) => {
      const row = group.representative;
      const { naicsName, naicsCode } = getNaicsFields(row);
      const companyTitle =
        cleanDisplay(row.visitor_company_name) ||
        cleanDisplay(row.visitor_account_name);
      const country = deriveCountry(row);
      const state = getStateDisplay(row.visitor_state, row.visitor_country || country);
      const pages = group.pages || [];
      const pagesList = pages
        .map((p) => cleanDisplay(p.url))
        .filter(Boolean)
        .join("; ");

      return {
        "Visit Date": row.last_seen
          ? dayjs(row.last_seen).format("YYYY-MM-DD")
          : "",
        Company: companyTitle,
        "State/Province": state,
        Country: country,
        NAICS: naicsName || naicsCode || "",
        "Lead Score": group.leadScore ?? "",
        "Duration (seconds)": group.durationSeconds ?? "",
        "Pages Count": group.pagesCount ?? pages.length,
        "Pages (URLs)": pagesList,
      };
    });

    try {
      const XLSX = await import("xlsx");
      const worksheet = XLSX.utils.json_to_sheet(rowsForExcel);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Website Visits");
      XLSX.writeFile(workbook, "website_visits_export.xlsx");
    } catch (err) {
      showSnackbar("error", "Failed to export selected visits.");
    }
  }, [
    getSelectedGroups,
    getNaicsFields,
    cleanDisplay,
    getStateDisplay,
    deriveCountry,
    showSnackbar,
  ]);

  const handleSaveSelectedVisit = useCallback(async () => {
    const selectedGroups = getSelectedGroups();

    if (selectedGroups.length === 0) {
      showSnackbar("info", "Check at least one visit row first.");
      return;
    }

    try {
      const results = await Promise.allSettled(
        selectedGroups.map((group) => {
          const row = group.representative;
          const { naicsName, naicsCode } = getNaicsFields(row);

          const name =
            cleanDisplay(row.visitor_company_name) ||
            cleanDisplay(row.visitor_account_name) ||
            "";

          const city = cleanDisplay(row.visitor_city) || null;
          const country = deriveCountry(row) || null;
          const state =
            getStateDisplay(row.visitor_state, row.visitor_country || country) || null;
          const county = cleanDisplay(row.visitor_county) || null;
          const phone = cleanDisplay(row.visitor_phone) || null;
          const website = cleanDisplay(row.visitor_website) || null;
          const domain = cleanDisplay(row.visitor_domain) || null;
          const description = cleanDisplay(row.visitor_description) || null;

          const durationSeconds = group.durationSeconds ?? 0;
          const leadScore = group.leadScore;

          if (!name) {
            return Promise.reject(
              new Error("Missing company name on this visit."),
            );
          }

          const payload = {
            dynamics_account_id: cleanDisplay(row.visitor_account_id) || null,
            external_visit_id: cleanDisplay(row.external_id) || null,

            name,
            city,
            county,
            state,
            country,
            phone_number: phone,
            number_of_employees:
              row.visitor_employees != null ? row.visitor_employees : null,
            website,
            domain,
            description,

            lead_score: leadScore,
            tags: normalizeProspectingTags(group.representative?.combined_tags),
            sector: getSector(row) || null,

            naics_id: null,
            naics_name: naicsName || null,
            naics_code: naicsCode || null,
            primary_naics_bucket:
              cleanDisplay(row.primary_naics_bucket) || null,
            naics_buckets: Array.isArray(row.naics_buckets)
              ? row.naics_buckets
              : null,
            naics_extra: row.naics_extra ?? null,

            visit_count: row.visit_count != null ? row.visit_count : null,
            last_seen: row.last_seen ?? null,

            page_url: cleanDisplay(row.page_url) || null,
            landing_page: cleanDisplay(row.landing_page) || null,
            utm_source: cleanDisplay(row.utm_source) || null,
            utm_medium: cleanDisplay(row.utm_medium) || null,
            utm_campaign: cleanDisplay(row.utm_campaign) || null,
            utm_term: cleanDisplay(row.utm_term) || null,
            utm_content: cleanDisplay(row.utm_content) || null,

            duration: row.duration != null ? String(row.duration) : null,
            duration_seconds: durationSeconds != null ? durationSeconds : null,
          };

          return api.post("/prospecting-companies/", payload);
        }),
      );

      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;

      const accountGuids = new Set(
        selectedGroups
          .map((group) => cleanDisplay(group.representative.visitor_account_id))
          .filter(Boolean),
      );

      if (accountGuids.size > 0) {
        await Promise.allSettled(
          Array.from(accountGuids).map((rawGuid) => {
            const guid = normalizeGuid(rawGuid);
            if (!guid) return Promise.resolve();
            return api.get(`/website-visits/${guid}/contacts`);
          }),
        );
      }

      if (succeeded > 0 && failed === 0) {
        showSnackbar(
          "success",
          `Saved ${succeeded} compan${
            succeeded === 1 ? "y" : "ies"
          } and their associated contacts.`,
        );
      } else if (succeeded > 0 && failed > 0) {
        showSnackbar(
          "warning",
          `Saved ${succeeded} compan${
            succeeded === 1 ? "y" : "ies"
          }, but ${failed} failed. Contacts were synced where possible.`,
        );
      } else {
        showSnackbar("error", "Failed to save selected companies.");
      }
    } catch (err) {
      console.error(
        "Failed to save companies and sync contacts to Prospecting",
        err,
      );
      showSnackbar("error", "Failed to save selected companies and contacts.");
    }
  }, [
    getSelectedGroups,
    showSnackbar,
    getNaicsFields,
    cleanDisplay,
    getStateDisplay,
    deriveCountry,
    getSector,
    normalizeGuid,
  ]);

  const handleSaveSelectedBRE = useCallback(async () => {
    const selectedGroups = getSelectedGroups();

    if (selectedGroups.length === 0) {
      showSnackbar("info", "Check at least one visit row first.");
      return;
    }

    try {
      const enriched = await Promise.all(
        selectedGroups.map(async (group) => {
          const row = group.representative;
          const rawGuid = cleanDisplay(row.visitor_account_id);
          const guid = rawGuid ? normalizeGuid(rawGuid) : null;

          if (!guid) {
            return { group, contacts: [] };
          }

          try {
            const res = await api.get(`/sophie-companies/${guid}/contacts`);

            const contactsMapped = (res.data || [])
              .map(mapDynamicsContactToBREContact)
              .filter(Boolean);

            return { group, contacts: contactsMapped };
          } catch (err) {
            return { group, contacts: [] };
          }
        }),
      );

      const payload = enriched
        .map(({ group, contacts }) => {
          const row = group.representative;
          const { naicsName, naicsCode } = getNaicsFields(row);

          const name =
            cleanDisplay(row.visitor_company_name) ||
            cleanDisplay(row.visitor_account_name);

          if (!name) return null;

          const loc = normalizeLocationForBRE({
            city: cleanDisplay(row.visitor_city) || null,
            state: cleanDisplay(row.visitor_state) || null,
            country: cleanDisplay(row.visitor_country) || null,
          });

          return {
            dynamics_id: cleanDisplay(row.visitor_account_id) || null,
            name,

            sector: getSector(row) || null,
            naics_name: naicsName || null,
            naics_code: naicsCode || null,

            phone_number: cleanDisplay(row.visitor_phone) || null,
            city: loc.city,
            state: loc.state,

            number_of_employees:
              row.visitor_employees != null ? row.visitor_employees : null,

            website: cleanDisplay(row.visitor_website) || null,
            description: cleanDisplay(row.visitor_description) || null,

            contacts,
          };
        })
        .filter(Boolean);

      if (payload.length === 0) {
        showSnackbar("warning", "No valid companies found to save.");
        return;
      }

      await api.post("/user-companies/batch", payload);

      showSnackbar(
        "success",
        `Saved ${payload.length} compan${
          payload.length === 1 ? "y" : "ies"
        } to BR&E Companies.`,
      );
    } catch (err) {
      showSnackbar("error", "Failed to save selected companies to BR&E.");
    }
  }, [
    getSelectedGroups,
    showSnackbar,
    cleanDisplay,
    normalizeGuid,
    mapDynamicsContactToBREContact,
    getNaicsFields,
    normalizeLocationForBRE,
    getSector,
  ]);

  return {
    handleExportSelected,
    handleSaveSelectedVisit,
    handleSaveSelectedBRE,
    mapDynamicsContactToBREContact,
  };
}
